import { logger } from "../services/logger.ts";
import React, { useState, useEffect } from 'react';
import { useSettings } from "../lib/settings/settingsContext.tsx";
import { supabase } from "../lib/db.ts";
import { MarketEngine } from "../lib/marketEngine.ts";
import { BetTransactionService } from "../services/BetTransactionService.ts";
import { isBelowMinTradeZar } from "../lib/tradeLimits.ts";
import { useMinTradeZar } from "../hooks/useMinTradeZar.ts";
import { Market, UserProfile, MarketStatus } from "../types.ts";
import { Loader2 } from 'lucide-react';
import { RealtimeChannel } from '@supabase/supabase-js';

const marketEngine = new MarketEngine();

const AMOUNT_INCREMENTS = [10, 50, 200];

interface PoolBuyPanelProps {
  market: Market;
  userProfile: UserProfile | null;
  selectedOptionId: number | null;
  onCancel: () => void;
  onBetSuccess?: () => void | Promise<void>;
}

export function PoolBuyPanel({
  market,
  userProfile,
  selectedOptionId,
  onCancel,
  onBetSuccess
}: PoolBuyPanelProps) {
  logger.debug('PoolBuyPanel received market with:', {
    id: market.id,
    title: market.title,
    status: market.status,
    hasOptions: !!market.market_options?.length,
    isPrivate: market.is_private
  });

  const [betAmount, setBetAmount] = useState<string>('');
  const [isPlacingBet, setIsPlacingBet] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [futurePricing, setFuturePricing] = useState<{ odds: number; potentialWin: number } | null>(null);
  const [marketEnded, setMarketEnded] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [summaryExpanded, setSummaryExpanded] = useState<boolean>(false);
  const { formatCurrency, settings } = useSettings();
  const minTradeZar = useMinTradeZar();

  const effectiveServiceFeePercentage = settings.serviceFee;

  const amountNum = parseFloat(betAmount) || 0;
  const walletBalance = userProfile?.wallet_balance ?? 0;
  const belowMinTrade = isBelowMinTradeZar(amountNum, minTradeZar);
  const isInsufficientFunds = amountNum > 0 && amountNum > walletBalance;

  const isBetButtonDisabled = selectedOptionId === null
    || !betAmount
    || belowMinTrade
    || isInsufficientFunds
    || isPlacingBet
    || marketEnded
    || isAdmin;

  // Subscribe to realtime updates
  useEffect(() => {
    if (!userProfile?.id) return;

    const subscriptions: RealtimeChannel[] = [];

    const walletSubscription = supabase
      .channel('user-wallet-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_wallets',
          filter: `user_id=eq.${userProfile.id}`
        },
        (payload) => {
          logger.info('Wallet update received:', payload);
          if (payload.new && typeof payload.new === 'object' && 'balance' in payload.new) {
            const balance = Number(payload.new.balance);
            if (!isNaN(balance)) {
              // Update handled by parent component's subscription
            }
          }
        }
      )
      .subscribe();
    subscriptions.push(walletSubscription);

    const marketPoolSubscription = supabase
      .channel('market-pool-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_pools',
          filter: `market_id=eq.${market.id}`
        },
        (payload) => {
          logger.info('Market pool update received:', payload);
          if (payload.new && typeof payload.new === 'object' &&
              'market_option_id' in payload.new && 'amount' in payload.new) {
            logger.info(`Option ${payload.new.market_option_id} pool updated to ${payload.new.amount}`);

            const fetchUpdatedMarket = async () => {
              try {
                const { data: marketData, error: marketError } = await supabase
                  .from('markets')
                  .select('*, market_options(*), market_pools(*)')
                  .eq('id', market.id)
                  .single();

                if (marketError) {
                  logger.error('Error fetching updated market:', marketError);
                  return;
                }

                if (marketData) {
                  logger.info('Updated market data:', marketData);
                  // Update handled by parent component's subscription
                }
              } catch (err) {
                logger.error('Error refreshing market data:', err);
              }
            };

            fetchUpdatedMarket();
          }
        }
      )
      .subscribe();
    subscriptions.push(marketPoolSubscription);

    const marketOptionsSubscription = supabase
      .channel('market-option-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_options',
          filter: `market_id=eq.${market.id}`
        },
        (payload) => {
          logger.info('Market option update received:', payload);
          if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
            const optionId = Number(payload.new.id);
            if (!isNaN(optionId)) {
              // Update handled by parent component's subscription
            }
          }
        }
      )
      .subscribe();
    subscriptions.push(marketOptionsSubscription);

    const marketUpdateSubscription = supabase
      .channel('market-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'markets',
          filter: `id=eq.${market.id}`
        },
        (payload) => {
          logger.info('Market update received:', payload);

          if (!payload.new || typeof payload.new !== 'object') return;

          if ('status' in payload.new && typeof payload.new.status === 'string') {
            const newStatus = payload.new.status as MarketStatus;
            if (newStatus !== market.status) {
              // Update handled by parent component's subscription
            }
          }

          if ('trading_end_time' in payload.new) {
            const newEndTime = String(payload.new.trading_end_time || '');
            if (newEndTime !== market.trading_end_time) {
              // Update handled by parent component's subscription
            }
          }
        }
      )
      .subscribe();
    subscriptions.push(marketUpdateSubscription);

    const betPlacedSubscription = supabase
      .channel('bet-placed-events')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_bets'
        },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'market_id' in payload.new) {
            const betMarketId = Number(payload.new.market_id);
            if (!isNaN(betMarketId) && betMarketId === Number(market.id)) {
              logger.info('New trade placed on this market:', payload);
            }
          }
        }
      )
      .subscribe();
    subscriptions.push(betPlacedSubscription);

    return () => {
      subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    };
  }, [userProfile?.id, market.id]);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      const { data } = await supabase.auth.getUser();
      const userRole = data?.user?.app_metadata?.role;
      setIsAdmin(userRole === 'admin' || userRole === 'superuser');
    };

    checkAdmin();
  }, []);

  const marketOptions = Array.isArray(market.market_options) ? market.market_options : [];
  const selectedOption = marketOptions.find(opt => opt.id === selectedOptionId);
  const selectedOptionIndex = marketOptions.findIndex(opt => opt.id === selectedOptionId);

  // Reset form when selected option ID changes
  useEffect(() => {
    setBetAmount('');
    calculateFuturePricing();
  }, [selectedOptionId, market.id]);

  useEffect(() => {
    if (!success && selectedOptionId !== null) {
      logger.info('Clearing messages due to selectedOptionId change:', selectedOptionId);
      setError(null);
    }
  }, [selectedOptionId, success]);

  // Retrieve success message from localStorage on component mount
  useEffect(() => {
    const savedSuccessMessage = localStorage.getItem('wayja_bet_success');
    if (savedSuccessMessage) {
      logger.info('Retrieved saved success message:', savedSuccessMessage);
      setSuccess(savedSuccessMessage);
      localStorage.removeItem('wayja_bet_success');
    }
  }, []);

  // Calculate future pricing when bet amount or selected option changes
  useEffect(() => {
    if (selectedOptionId !== null) {
      calculateFuturePricing();
    }
  }, [betAmount, selectedOptionId, market.id]);

  const calculateFuturePricing = () => {
    if (!Array.isArray(market.market_options)) {
      logger.error('Market options is not an array:', market.market_options);
      return;
    }
    if (selectedOptionIndex < 0 || selectedOptionId === null) {
      setFuturePricing(null);
      return;
    }

    logger.info('Calculating pricing for market:', market.id);
    logger.info('Selected option ID:', selectedOptionId);
    logger.info('Selected option index:', selectedOptionIndex);
    logger.info('Market options:', market.market_options.map(o => ({ id: o.id, name: o.name })));

    try {
      const pricing = marketEngine.calculatePricing(
        {
          id: market.id,
          title: market.title,
          is_private: market.is_private,
          is_dynamic: true,
          category: market.category,
          description: market.description,
          created_by: market.created_by,
          status: market.status,
          total_bets: market.total_bets + (parseFloat(betAmount) || 0),
          market_options: market.market_options,
          market_pools: market.marketPools,
          resolution_time: market.resolution_time
        },
        selectedOptionId,
        parseFloat(betAmount) || 0
      );

      const amountAfterFee = Number(((parseFloat(betAmount) || 0) * (1 - effectiveServiceFeePercentage)).toFixed(6));
      const returnOnDollar = Math.floor(pricing.returnOnDollar * 1000000) / 1000000;
      const potentialWin = Number((amountAfterFee * returnOnDollar).toFixed(6));
      setFuturePricing({
        odds: returnOnDollar,
        potentialWin
      });
    } catch (error) {
      logger.error("Error in calculateFuturePricing:", error);
      setFuturePricing(null);
    }
  };

  // Check if market has ended trading
  useEffect(() => {
    const checkTradingEnded = () => {
      const raw = market.trading_end_time || market.resolution_time;
      if (!raw) return false;
      const endTime = new Date(raw);
      if (Number.isNaN(endTime.getTime())) return false;
      return new Date() >= endTime;
    };

    const hasTradingEnded = checkTradingEnded();
    setMarketEnded(hasTradingEnded);

    const intervalId = setInterval(() => {
      const hasEndedNow = checkTradingEnded();
      if (hasEndedNow && !marketEnded) {
        setMarketEnded(true);
        setError('Market trading has ended. No more trades allowed.');
      } else if (!hasEndedNow && marketEnded) {
        setMarketEnded(false);
        setError(null);
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [market.trading_end_time, market.resolution_time, marketEnded]);

  const isOptionValid = !!selectedOption && selectedOption.status === 'active';
  const isOptionEliminated = !!selectedOption && selectedOption.status === 'eliminated';

  if (market.status !== 'active') {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="p-6">
          <div className="flex items-start bg-red-50 p-4 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-red-800 font-medium mb-1">Market Not Tradeable</h3>
              <p className="text-sm text-red-700">
                {market.status === 'ended' && "This market has ended and is no longer accepting trades."}
                {market.status === 'expired' && "This market has expired and is no longer accepting trades."}
                {market.status === 'resolved' && "This market has been resolved and is no longer accepting trades."}
                {market.status === 'cancelled' && "This market has been cancelled and is no longer accepting trades."}
                {market.status === 'pending_approval' && "This market is pending approval and is not yet accepting trades."}
                {market.status === 'rejected' && "This market has been rejected and is not accepting trades."}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="w-full px-4 py-2 mt-4 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!isOptionValid) {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-4">
            {isOptionEliminated ? (
              <div className="flex items-start bg-red-50 p-4 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h3 className="text-red-800 font-medium mb-1">Option Eliminated</h3>
                  <p className="text-sm text-red-700">
                    This option has been eliminated from the knockout tournament and is no longer available for trading.
                    Please select another active option.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-600">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>Invalid option selected. Please select a valid trading option.</span>
              </div>
            )}
          </div>
          <button
            onClick={onCancel}
            className="w-full px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const handlePlaceBet = async () => {
    setError(null);
    setSuccess(null);

    if (marketEnded) {
      setError('This market has ended and is no longer accepting trades');
      return;
    }
    if (!selectedOption) {
      setError('Please select an outcome to trade on');
      return;
    }
    const amount = parseFloat(betAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid trade amount');
      return;
    }
    if (isBelowMinTradeZar(amount, minTradeZar)) {
      setError(`Minimum trade amount is R${minTradeZar}.`);
      return;
    }
    const walletBalance = userProfile?.wallet_balance || 0;
    if (amount > walletBalance) {
      setError(`Insufficient funds. Your balance: ${formatCurrency(walletBalance)}`);
      return;
    }

    setIsPlacingBet(true);
    try {
      logger.info(`Placing trade: market=${market.id}, optionId=${selectedOption.id}, amount=${amount}, fee=${effectiveServiceFeePercentage}`);

      const bet = await BetTransactionService.placeBet(
        market.id,
        selectedOption.id,
        amount,
        effectiveServiceFeePercentage
      );

      if (bet) {
        const successMessage = `Successfully placed trade of ${formatCurrency(amount)} on ${selectedOption.name}`;
        logger.info('Setting success message:', successMessage);
        setSuccess(successMessage);
        setBetAmount('');
        if (onBetSuccess) await onBetSuccess();
      }
    } catch (err) {
      logger.error('Error placing trade:', err);

      let errorMessage = 'Failed to place trade';
      if (err instanceof Error) {
        errorMessage += ': ' + err.message;
      } else if (typeof err === 'object' && err !== null) {
        const errObj = err as Record<string, unknown>;
        if (errObj.message) errorMessage += ': ' + String(errObj.message);
        if (errObj.details) errorMessage += ' - ' + String(errObj.details);
        if (errObj.hint) errorMessage += ' (' + String(errObj.hint) + ')';
      }

      setError(errorMessage);
    } finally {
      setIsPlacingBet(false);
    }
  };

  const setMaxAmount = () => {
    if (userProfile?.wallet_balance) {
      setBetAmount(userProfile.wallet_balance.toFixed(2));
    }
  };

  const getBalancePercentage = () => {
    if (!userProfile?.wallet_balance || parseFloat(betAmount) <= 0) return 0;
    return Math.min((parseFloat(betAmount) / userProfile.wallet_balance) * 100, 100);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 relative z-30 shadow-sm hover:shadow-md transition-shadow duration-200">
      <h3 className="text-xl font-medium text-gray-900 mb-4">Place Your Trade</h3>

      {marketEnded && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-700 animate-pulse">
          This market has ended and is no longer accepting trades.
        </div>
      )}

      {isAdmin && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700">
          <strong>Admin Notice:</strong> As an admin, you are not allowed to place trades to ensure fair play and maintain system integrity.
        </div>
      )}

      {error && !marketEnded && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 animate-pulse">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 animate-pulse">
          {success}
        </div>
      )}

      <div className="mb-5">
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-50/80 transition-colors">
          <div className="flex justify-between items-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Selected Outcome
              </label>
              <span className="font-medium text-lg">{selectedOption?.name ?? 'None Selected'}</span>
            </div>
            {selectedOption && (
              <div className="text-right">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Returns
                </label>
                <span className="text-wayja-teal font-bold text-lg">
                  {futurePricing
                    ? futurePricing.odds.toFixed(2)
                    : (selectedOption.odds ?? 0).toFixed(2)}x
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-col">
          <div className="flex justify-between items-center mb-2">
            <label className="text-lg font-medium text-gray-900">
              Amount
            </label>
            <div className="text-sm text-gray-600 flex items-center gap-1">
              <span>Balance: </span>
              <span className="font-semibold">{formatCurrency(userProfile?.wallet_balance || 0)}</span>
            </div>
          </div>

          <div className="h-1 w-full bg-gray-200 rounded-full mb-3 overflow-hidden">
            <div
              className="h-full bg-wayja-teal rounded-full transition-all duration-300 ease-out"
              style={{ width: `${getBalancePercentage()}%` }}
            ></div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 transition-all shadow-sm focus-within:ring-2 focus-within:ring-gray-300">
            <div className="flex items-center justify-between">
              <span className="text-wayja-teal text-2xl font-medium">{settings.currency.symbol}</span>
              <input
                type="number"
                className="bg-transparent text-gray-900 text-right text-4xl w-full focus:outline-none placeholder-gray-400"
                value={betAmount}
                onChange={(e) => setBetAmount(e.target.value)}
                min={minTradeZar}
                step="0.01"
                disabled={isPlacingBet || marketEnded}
                placeholder="0.00"
              />
            </div>
          </div>

          {belowMinTrade && (
            <p className="text-sm text-amber-700 mb-3" role="alert">
              Minimum trade amount is R{minTradeZar}.
            </p>
          )}
          {isInsufficientFunds && (
            <p className="text-sm text-amber-700 mb-3" role="alert">
              Insufficient balance
            </p>
          )}

          <div className="grid grid-cols-4 gap-3">
            {AMOUNT_INCREMENTS.map(amount => (
              <button
                key={amount}
                onClick={() => {
                  const currentAmount = parseFloat(betAmount) || 0;
                  setBetAmount((currentAmount + amount).toFixed(2));
                }}
                disabled={isPlacingBet || marketEnded}
                className="bg-white hover:bg-gray-50 text-gray-700 py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 text-sm font-medium border border-gray-200 hover:border-gray-300 hover:scale-105 active:scale-95 shadow-sm"
              >
                +{settings.currency.symbol}{amount}
              </button>
            ))}
            <button
              onClick={setMaxAmount}
              disabled={isPlacingBet || marketEnded || !userProfile?.wallet_balance}
              className="bg-white hover:bg-gray-50 text-wayja-teal py-2 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 text-sm font-medium border border-gray-200 hover:border-gray-300 hover:scale-105 active:scale-95 shadow-sm"
            >
              Max
            </button>
          </div>
        </div>
      </div>

      {selectedOption && betAmount && !isNaN(parseFloat(betAmount)) && parseFloat(betAmount) > 0 && (
        <div className="mb-5 bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-5 flex justify-between items-center">
            <h4 className="font-semibold text-gray-700">Potential Win:</h4>
            <div className="flex items-center gap-2">
              <span className="text-green-600 font-bold text-xl">
                {futurePricing
                  ? formatCurrency(futurePricing.potentialWin, 2)
                  : formatCurrency(
                      Number(
                        (parseFloat(betAmount) * (1 - effectiveServiceFeePercentage) * (selectedOption.odds ?? 0)).toFixed(6)
                      ),
                      2
                    )}
              </span>
              <button
                onClick={() => setSummaryExpanded(!summaryExpanded)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-200 ${summaryExpanded ? 'rotate-180' : ''}`}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
            </div>
          </div>

          {summaryExpanded && (
            <div className="px-5 pb-5 pt-0 border-t border-gray-200 space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Trade Amount:</span>
                <span className="font-medium">{formatCurrency(parseFloat(betAmount), 2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Returns:</span>
                <span className="font-medium">
                  {futurePricing
                    ? `${futurePricing.odds.toFixed(2)}x`
                    : `${(selectedOption.odds ?? 0).toFixed(2)}x`}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all duration-200 disabled:opacity-50 active:scale-95"
            onClick={onCancel}
            disabled={isPlacingBet}
          >
            Cancel
          </button>
        )}
        <button
          className={`px-5 py-2.5 text-white rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow active:scale-95 flex items-center justify-center ${
            isBetButtonDisabled
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-wayja-teal hover:bg-wayja-teal/90'
          }`}
          onClick={handlePlaceBet}
          disabled={isBetButtonDisabled}
        >
          {isPlacingBet ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
          {isPlacingBet ? 'Placing Trade...' : 'Place Trade'}
        </button>
      </div>
    </div>
  );
}
